import { ReactNode } from 'react';
import { cn } from '@/shared/utils/utils';

export interface DashboardGridProps {
  children: ReactNode;
  className?: string;
  gap?: number;
}

/** Twelve columns from md up; one column below it. Laptop and Android-sized
 *  viewports get the same tree, so a tile never becomes an orphaned half-width
 *  card on a narrow screen. */
export function DashboardGrid({
  children,
  className,
  gap = 4, // Tailwind gap-4 (1rem / 16px) — an ERP reads denser than a marketing page.
}: DashboardGridProps) {
  return (
    <div
      className={cn('grid w-full auto-rows-min grid-cols-1 gap-4 md:grid-cols-12', className)}
      style={{ gap: `${gap * 0.25}rem` }}
    >
      {children}
    </div>
  );
}

// Tailwind's JIT only sees literal class strings, so every span is spelled out.
const SPAN_CLASS: Record<number, string> = {
  1: 'md:col-span-1', 2: 'md:col-span-2', 3: 'md:col-span-3', 4: 'md:col-span-4',
  5: 'md:col-span-5', 6: 'md:col-span-6', 7: 'md:col-span-7', 8: 'md:col-span-8',
  9: 'md:col-span-9', 10: 'md:col-span-10', 11: 'md:col-span-11', 12: 'md:col-span-12',
};

export interface GridItemProps {
  children: ReactNode;
  /** Span columns (1-12) from md up; always full width below md. */
  w?: number;
  /** Retained for the layout contract; height is content-driven. */
  h?: number;
  className?: string;
}

export function GridItem({ children, w = 12, className }: GridItemProps) {
  return (
    <div className={cn('col-span-1 w-full', SPAN_CLASS[w] || SPAN_CLASS[12], className)}>
      {children}
    </div>
  );
}
